import React, { CSSProperties } from 'react'

const APP_FONT = "'F-Default', Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
const MONO_FONT = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace'

const headingStyle: CSSProperties = { margin: '16px 0 4px', fontSize: '13px', fontWeight: 600, color: 'var(--tma-text-strong)' }
const textStyle: CSSProperties = { margin: '0 0 6px', fontSize: '13px', lineHeight: 1.5, color: 'var(--tma-text-strong)' }
const listStyle: CSSProperties = { ...textStyle, paddingLeft: '20px' }
const codeStyle: CSSProperties = { fontFamily: MONO_FONT, fontSize: '12px' }

interface CertificateIssuersHelpProps {
  // The cluster this TMA writes issuers to; null when cluster sync is off.
  clusterName: string | null
}

// What the Certificates page does, for operators who don't read the code. Keep it in step
// with CertificateIssuerController, TenantCertificateController and KubernetesClusterIssuerSync.
const CertificateIssuersHelp: React.FC<CertificateIssuersHelpProps> = ({ clusterName }) => (
  <div style={{ fontFamily: APP_FONT, maxHeight: '65vh', overflowY: 'auto', paddingRight: '8px' }}>
    <p style={textStyle}>
      An issuer is a CA account that cert-manager requests TLS certificates from over ACME, such as Sectigo OV. Tenant hostnames use the
      platform default (<span style={codeStyle}>letsencrypt-prod</span>) unless they are pointed at an issuer from this list.
    </p>

    <h4 style={headingStyle}>What Add issuer does</h4>
    <ol style={listStyle}>
      <li>
        It checks the input. The name must be a Kubernetes name and can't be changed later. The ACME server must be an https URL. The EAB
        key ID and HMAC key go together, or both stay empty for a CA without external account binding (such as Let&apos;s Encrypt).
      </li>
      <li>
        {clusterName ? (
          <>
            In <b>{clusterName}</b> it stores the HMAC key as Secret <span style={codeStyle}>&lt;name&gt;-eab</span> in the{' '}
            <span style={codeStyle}>cert-manager</span> namespace. Then it creates ClusterIssuer <span style={codeStyle}>&lt;name&gt;</span>
            , labelled <span style={codeStyle}>managed-by=tma</span>, which answers HTTP-01 challenges through the nginx ingress.
          </>
        ) : (
          <>Cluster sync is off in this environment, so nothing is written to a cluster: the issuer is only stored.</>
        )}
      </li>
      <li>Only once the cluster accepts it is the issuer saved in TMA. The HMAC key is stored encrypted (SQL Always Encrypted).</li>
      <li>
        cert-manager then registers an ACME account with the CA. <b>Ready</b> means the CA accepted it. <b>Not ready</b> usually means a
        wrong server URL or wrong EAB credentials; hover the status to see what the CA answered.
      </li>
    </ol>
    <p style={textStyle}>
      Adding an issuer changes nothing for tenants yet. To use it, open the tenant, go to <b>Runtime</b> and then <b>Certificates</b>, and
      pick the issuer for its Suite or Portal UI hostname. TMA switches the live ingress, cert-manager issues a new certificate, and the
      next deploy keeps that choice.
    </p>

    <h4 style={headingStyle}>How long does this list get?</h4>
    <p style={textStyle}>
      One entry per CA account or product, not per certificate or tenant. Requesting or renewing certificates never adds an issuer; only an
      admin does. Each TMA (nonproduction and production) has its own list for its own cluster, so an issuer needed in both is added in
      both.
    </p>
    <p style={textStyle}>
      On the tenant, the issuer field is searchable: type part of the display name, the ClusterIssuer name or the description. A good
      description (when to use it, who owns the CA account) makes the right issuer easy to find.
    </p>

    <h4 style={headingStyle}>The other actions</h4>
    <ul style={listStyle}>
      <li>
        <b>Edit</b> updates the ClusterIssuer. An empty HMAC key field keeps the stored key; clearing the key ID removes EAB.
      </li>
      <li>
        <b>Show the HMAC key</b> (admins) shows the stored key, so it doesn&apos;t need to be kept somewhere else as well. Each time it is
        shown is recorded in the audit log.
      </li>
      <li>
        <b>Re-apply</b> writes the issuer to the cluster again, for example after someone deleted it there.
      </li>
      <li>
        <b>Remove</b> only works when no hostname uses the issuer. It deletes the ClusterIssuer and its Secrets from the cluster.
      </li>
      <li>
        Clicking an issuer opens its <b>certificates and requests</b>: what cert-manager issued, what failed and why, and <b>Renew</b> to
        issue a certificate again right away.
      </li>
    </ul>
    <p style={textStyle}>TMA never changes or deletes a ClusterIssuer it did not create.</p>
  </div>
)

export default CertificateIssuersHelp
